// Utility-method to build the payload for normalization (expiration date, etc.)
// Add/remove fields in RENTAL_VERSION_NORMALIZE_FIELDS as needed for future backend logic
export const RENTAL_VERSION_NORMALIZE_FIELDS = [
  'id',
  'name',
  'scheduleAttributes',
  'createdAt',
  'deliveryType',
  'deliveryAddressStreetAddress1',
  'deliveryAddressStreetAddress2',
  'deliveryAddressCity',
  'deliveryAddressLocale',
  'deliveryAddressPostalCode',
  'deliveryAddressCountry',
  'customExpireDate',
  'extraTransportsAttributes',
];

// The extra delivery / pickup row fields sent to normalize (TD1-10271 §6.4): the API fields plus
// `rvId`, so that whatever echoes a row can only ever merge back onto the same store row (never
// append a copy); the read-only `label` never travels. `endDateTime` is the window end (D-9 amended).
export const EXTRA_TRANSPORT_PAYLOAD_FIELDS = ['rvId', 'id', 'transportType', 'startDateTime', 'endDateTime', '_destroy'];

const pickExtraTransportFields = (rows: unknown) =>
  Array.isArray(rows)
    ? rows.map((row) => {
        const picked: Record<string, unknown> = {};
        for (const key of EXTRA_TRANSPORT_PAYLOAD_FIELDS) {
          if (row?.[key] !== undefined) picked[key] = row[key];
        }
        return picked;
      })
    : rows;

// Every outbound payload that carries the form's extra delivery / pickup rows sends them through
// here, so `EXTRA_TRANSPORT_PAYLOAD_FIELDS` above is the single definition of what a row sends.
//
// It matters most for `label`: the labels are the API's to compute and it recomputes them on save
// (D-29), so a stored "Drop Off 3" travelling next to a freshly flipped `transport_type: pick_up`
// is a stale value contradicting the row it is attached to — a trap for the next reader at best,
// and something the API could act on at worst.
export const withExtraTransportPayloadRows = <T extends { extraTransportsAttributes?: unknown }>(
  data: T
): T => {
  if (data?.extraTransportsAttributes === undefined) return data;

  return { ...data, extraTransportsAttributes: pickExtraTransportFields(data.extraTransportsAttributes) };
};

export function buildRentalVersionNormalizePayload(data: any) {
  const payload: any = {};
  for (const key of RENTAL_VERSION_NORMALIZE_FIELDS) {
    if (data[key] !== undefined) payload[key] = data[key];
  }
  if (payload.extraTransportsAttributes !== undefined) {
    payload.extraTransportsAttributes = pickExtraTransportFields(payload.extraTransportsAttributes);
  }
  return payload;
}
